import { types } from "mobx-state-tree";
/**
 * This is the serializable version of IDocumentMetadata. It is almost the same. The one
 * important difference is the `properties` property.
 * In this MST model that property is an `observable.map<string, string>`.
 * In the IDocumentMetadata it is a Record<string,string>.
 */
export const DocumentMetadataModel = types.model("DocumentMetadata", {
    uid: types.string,
    type: types.string,
    key: types.identifier,
    createdAt: types.maybe(types.number),
    /**
     * If the document is a group document this is the id the group that owns the document.
     * If the document is not a group document this should be undefined because the group
     * of the user of the document might change and we don't want to store stale group ids,
     * or try to keep them all updated.
     */
    groupId: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    originDoc: types.maybeNull(types.string),
    properties: types.map(types.union(types.string, types.number)),
    tools: types.array(types.string),
    strategies: types.array(types.string),
    investigation: types.maybeNull(types.string),
    problem: types.maybeNull(types.string),
    unit: types.maybeNull(types.string),
    visibility: types.maybeNull(types.string),
    lastHistoryEntry: types.maybeNull(types.model({
        id: types.string,
        index: types.number,
    })),
})
    .views((self) => ({
    get propertiesAsStringRecord() {
        const record = {};
        self.properties.forEach((value, key) => {
            record[key] = String(value);
        });
        return record;
    },
    /**
     * In the database there can be properties with numeric values.
     * For consistency with the other document types we always return these as Strings.
     * @param key
     */
    getProperty(key) {
        const value = self.properties.get(key);
        if (value === undefined) {
            return undefined;
        }
        return String(value);
    },
}));
//
// Compile-time type checks
// =========================
// These make sure that IDocumentMetadataModel is compatible with IDocumentMetadata.
// The `properties` field is omitted because the model uses an observable map while
// the interface uses Record<string, string>.
/**
 * This is function is not called anywhere. Its existence forces the Typescript compiler
 * to check the basic compatibility between IDocumentMetadataModel and IDocumentMetadata.
 * Since many of the properties are optional, this check mainly catches type mismatches.
 */
// eslint-disable-next-line unused-imports/no-unused-vars
function _checkDocumentMetadataModelMatchesInterface(_model) {
    return _model;
}
/**
 * This is function is not called anywhere. Its existence forces the Typescript compiler
 * to check that all of the properties match between IDocumentMetadataModel and IDocumentMetadata.
 * It does this by making all properties required.
 * The createdAt property is excluded because in the MST model it is maybe, which results in
 * a typescript type of `number | undefined`, which doesn't get stripped out by the Required<T>
 * utility type.
 */
// eslint-disable-next-line unused-imports/no-unused-vars
function _checkDocumentMetadataModelMatchesFullInterface(_model) {
    return _model;
}
/**
 * This is function is not called anywhere. Its existence forces the Typescript compiler
 * to check that the snapshot type of DocumentMetadataModel matches IDocumentMetadata.
 * The DocumentMetadataModel is created from the data in Firestore which is supposed
 * to match the IDocumentMetadata interface. So this checks to make sure at least
 * the types support that creation. The types are modified to have all required
 * properties so it will pick up missing properties.
 */
// eslint-disable-next-line unused-imports/no-unused-vars
function _checkInterfaceMatchesDocumentMetadataModelSnapshotIn(_model) {
    return _model;
}
//
// End of compile-time type checks
//
//# sourceMappingURL=data:application/json;base64,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